import { Slot } from '@radix-ui/react-slot'
import clsx from 'clsx'
import * as React from 'react'

import { cn } from '@/lib/utils'

const buttonClasses = ({
  variant = 'solid',
  size = 'medium',
  loading = false,
}: {
  variant: 'solid' | 'outline' | 'light' | 'plain' | 'light-outline'
  size?: 'small' | 'medium' | 'large'
  loading?: boolean
}) => {
  const variantClasses = {
    solid: `text-white hover:bg-primary-700 font-medium ${loading ? 'bg-primary-700' : 'bg-primary-500'} `,
    outline: `border-2 border-primary-500 text-primary-500 font-medium hover:bg-primary-500 hover:border-primary-500 hover:text-white ${loading ? 'bg-primary-500' : 'bg-transparent'}`,
    light: ` text-black hover:bg-accent-7 hover:text-primary-500 font-medium ${loading ? 'bg-accent-7' : 'bg-white'}`,
    plain: ` text-primary-500 hover:underline font-semibold`,
    'light-outline': `border-2 border-white text-white font-medium hover:bg-white hover:white hover:text-primary-500 ${loading ? 'bg-primary-500' : 'bg-transparent'}`,
  }

  const sizeClasses = {
    small: 'h-10 px-6',
    medium: 'h-12 px-6',
    large: 'h-14 px-6',
  }

  return clsx(variantClasses[variant], sizeClasses[size], '')
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
  size?: 'small' | 'medium' | 'large'
  variant?: 'solid' | 'outline' | 'light' | 'plain' | 'light-outline'
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant = 'solid', size = 'medium', loading, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      className={cn(
        buttonClasses({ variant, size, loading }),
        'inline-flex min-w-24 items-center justify-center whitespace-nowrap rounded-full text-base ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none',
        variant === 'plain' ? 'disabled:text-grey-400' : 'disabled:border-grey-100 disabled:bg-grey-100 disabled:text-grey-400',
        className,
      )}
      ref={ref}
      {...props}
    />
  )
})
Button.displayName = 'Button'

export { Button }
